import React, { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, Trash2, Sparkles, Loader2 } from "lucide-react";
import { chatSend, chatHistory, chatClear } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SUGGESTIONS = [
  "Summarize my active positions",
  "What's my win-rate & net P&L?",
  "Which pattern is losing me money?",
  "Should I hold my current trades to close?",
];

export default function ChatAssistant() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && user) {
      chatHistory().then((d) => {
        setMsgs(d.messages || []);
        const last = (d.messages || []).slice(-1)[0];
        if (last?.session_id) setSessionId(last.session_id);
      }).catch(() => {});
    }
  }, [open, user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, sending]);

  if (!user) return null;

  const send = async (payload) => {
    const t = (payload ?? text).trim();
    if (!t || sending) return;
    setText("");
    setMsgs((m) => [...m, { role: "user", content: t, ts: new Date().toISOString() }]);
    setSending(true);
    try {
      const res = await chatSend(t, sessionId);
      setSessionId(res.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: res.reply, ts: new Date().toISOString() }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: `⚠️ ${e?.response?.data?.detail || "AI unavailable, try again in a moment."}`, ts: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    if (!confirm("Clear entire chat history?")) return;
    await chatClear();
    setMsgs([]);
    setSessionId(null);
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="chat-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full bg-[#FFCC00] text-black font-semibold shadow-2xl hover:scale-105 transition-transform"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm">AI Coach</span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div
          data-testid="chat-drawer"
          className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[420px] h-[70vh] max-h-[640px] bg-[#0a0a0a] border border-zinc-800 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-[#FFCC00]/10 to-transparent">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#FFCC00] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-white">NAVA-COACH</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">GPT-5.2 · Live-data grounded</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="chat-clear-btn" onClick={clearAll} title="Clear history" className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-500 hover:text-red-400">
                <Trash2 className="w-4 h-4" />
              </button>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} title="Close" className="p-1.5 rounded-md hover:bg-zinc-900 text-zinc-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {msgs.length === 0 && !sending && (
              <div className="space-y-3">
                <div className="text-sm text-zinc-400">
                  Hi {user.name?.split(" ")[0] || "trader"}. I can see your live positions, P&L and lessons. Ask me anything.
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => send(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#FFCC00] text-black rounded-br-sm"
                      : "bg-zinc-900 text-zinc-100 border border-zinc-800 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 bg-zinc-900 border border-zinc-800 text-zinc-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> <span className="text-xs">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-zinc-800 p-3 bg-[#0a0a0a]">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Ask about your trades, P&L, market bias…"
                rows={1}
                className="flex-1 resize-none max-h-24 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFCC00]/40"
              />
              <button
                data-testid="chat-send-btn"
                onClick={() => send()}
                disabled={!text.trim() || sending}
                className="p-2.5 rounded-lg bg-[#FFCC00] text-black hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-zinc-600 mt-1.5 px-1">Assistive analysis — not financial advice.</div>
          </div>
        </div>
      )}
    </>
  );
}
